import { userViewerSettingsAtom } from '@/atoms';
import { LayerData } from '@/atoms/layers.atom';
import { Billboard, Text } from '@react-three/drei';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import * as THREE from 'three';

/** Generate a deterministic random color from a layer ID */
function colorFromId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash % 360);
  return `hsl(${h}, 70%, 55%)`;
}

interface BoundingBoxProps {
  min: [number, number, number];
  max: [number, number, number];
  color: string;
  label: string;
}

function BoundingBox({ min, max, color, label }: BoundingBoxProps) {
  const { center, size, fontSize, textPosition } = useMemo(() => {
    const minV = new THREE.Vector3(...min);
    const maxV = new THREE.Vector3(...max);
    const c = minV.clone().add(maxV).multiplyScalar(0.5);
    const s = new THREE.Vector3().subVectors(maxV, minV);
    const fs = Math.max(Math.max(s.x, s.y, s.z) * 0.04, 0.3);
    const tp = new THREE.Vector3(c.x, c.y, c.z + s.z / 2 + fs * 1.5);
    return { center: c, size: s, fontSize: fs, textPosition: tp };
  }, [min, max]);

  return (
    <group>
      <lineSegments position={center} scale={size} renderOrder={998}>
        <edgesGeometry args={[new THREE.BoxGeometry(1, 1, 1)]} />
        <lineBasicMaterial color={color} transparent opacity={0.8} depthTest={false} />
      </lineSegments>
      <Billboard position={textPosition}>
        <Text
          fontSize={fontSize}
          color={color}
          outlineColor='black'
          outlineWidth={0.005}
          outlineBlur={0.005}
          anchorX='center'
          anchorY='bottom'
          depthOffset={-1}
          renderOrder={999}>
          {label}
        </Text>
      </Billboard>
    </group>
  );
}

interface PointCloudBoundingBoxesProps {
  layers: LayerData[];
}

export function PointCloudBoundingBoxes({ layers }: PointCloudBoundingBoxesProps) {
  const viewerSettings = useAtomValue(userViewerSettingsAtom);

  const boxes = useMemo(() => {
    if (!viewerSettings?.showPointCloudBoundingBoxes) return [];
    return layers
      .map((layer) => {
        const bb = layer.metadata?.potreeMetadata?.boundingBox;
        if (!bb?.min || !bb?.max) return null;
        return {
          id: layer.id,
          min: bb.min as [number, number, number],
          max: bb.max as [number, number, number],
          color: colorFromId(layer.id),
          label: layer.name,
        };
      })
      .filter(Boolean) as {
      id: string;
      min: [number, number, number];
      max: [number, number, number];
      color: string;
      label: string;
    }[];
  }, [layers, viewerSettings?.showPointCloudBoundingBoxes]);

  if (boxes.length === 0) return null;

  return (
    <>
      {boxes.map((box) => (
        <BoundingBox key={box.id} min={box.min} max={box.max} color={box.color} label={box.label} />
      ))}
    </>
  );
}
